import { useCallback, useEffect, useState } from 'react'
import { erfuelltRecht } from '@symphenia/shared-types'
import { cardTitle, formCardSm, inputBase } from '@/lib/form-styles'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { useTerminology } from '@/lib/useTerminology'
import {
  connectTimeBridge,
  disconnectTimeBridge,
  fetchTimeBridge,
  fetchUnlinkedRefs,
  ignoreRef,
  importRefWithNewNumber,
  importRefs,
  linkRef,
  rebuildMirror,
  searchProjectsForLink,
  type ImportResult,
  type MirrorResult,
  type TimeBridgeState,
  type UnlinkedRef,
} from '../api/time-bridge.api'
import { alsTag } from '../project-labels'
import { bestaetige } from '@/lib/dialoge'

const MIRROR_CODES: Record<string, string> = {
  MIRROR_NUMBER_TAKEN: 'Nummer ist in der Zeiterfassung schon vergeben — unten verknüpfen oder mit neuer Nummer übernehmen',
  MIRROR_FAILED: 'Spiegel fehlgeschlagen',
  IMPORT_FAILED: 'Übernahme fehlgeschlagen',
}

/**
 * AP-35 Detailblatt 4 6.1 und 6.6 — **die Zeiterfassung anschließen und ihre
 * Aufträge übernehmen.**
 *
 * Reihenfolge, wie ein Betrieb sie geht: zuerst übernehmen (Vorschau, dann
 * Ausführen), dann anschließen. Beides lässt sich auch danach noch tun — die
 * Liste der nicht verknüpften Aufträge bleibt erreichbar.
 */
export function TimeBridgeCard() {
  const terms = useTerminology()
  const { user } = useAuthStore()
  const darf =
    erfuelltRecht('projects.settings.manage', user?.permissions) && erfuelltRecht('time.selection.manage', user?.permissions)
  const darfAnlegen = darf && erfuelltRecht('projects.project.manage', user?.permissions)

  const [zustand, setZustand] = useState<TimeBridgeState | null>(null)
  const [liste, setListe] = useState<UnlinkedRef[]>([])
  const [mitAusgeblendeten, setMitAusgeblendeten] = useState(false)
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set())
  const [nummern, setNummern] = useState<'KEEP' | 'NEW'>('KEEP')
  const [vorschau, setVorschau] = useState<ImportResult | null>(null)
  const [ergebnis, setErgebnis] = useState<string | null>(null)
  const [spiegel, setSpiegel] = useState<MirrorResult | null>(null)
  const [verknuepfen, setVerknuepfen] = useState<UnlinkedRef | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const laden = useCallback(async () => {
    try {
      const [z, l] = await Promise.all([fetchTimeBridge(), darf ? fetchUnlinkedRefs(mitAusgeblendeten) : Promise.resolve([])])
      setZustand(z)
      setListe(l)
      setAuswahl((a) => new Set([...a].filter((id) => l.some((r) => r.id === id))))
    } catch (e) {
      setFehler(apiErrorText(e, 'Laden fehlgeschlagen.'))
    }
  }, [darf, mitAusgeblendeten])

  useEffect(() => {
    void laden()
  }, [laden])

  async function tun(f: () => Promise<void>) {
    setLaeuft(true)
    setFehler(null)
    try {
      await f()
      await laden()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    } finally {
      setLaeuft(false)
    }
  }

  if (!zustand) return <div className={formCardSm}>{fehler ?? 'Lädt …'}</div>

  const knopf = 'rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-40'
  const primaer = 'rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-40'

  return (
    <div className="space-y-4">
      <div className={formCardSm}>
        <h2 className={cardTitle}>Anschluss der Zeiterfassung</h2>
        {zustand.connected ? (
          <p className="text-sm">
            <span className="mr-2 inline-block h-2 w-2 rounded-full bg-green-500" />
            Angeschlossen — {terms.projectPlural}, Positionen und Arbeitsschritte werden hier geführt und in die Zeiterfassung
            gespiegelt. Gebuchte Stunden zählen als Ist.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Nicht angeschlossen — die Zeiterfassung führt ihre {terms.projectPlural} selbst ({zustand.projectSource}). Keine Ist-Stunden, kein Abgleich
            der Nummern.
          </p>
        )}
        {!zustand.projectTimeBooked && (
          <p className="mt-2 text-sm text-muted-foreground">Der Baustein „Projektzeit" der Zeiterfassung ist nicht gebucht — ohne ihn gibt es nichts anzuschließen.</p>
        )}
        {darf && zustand.projectTimeBooked && (
          <div className="mt-3 flex flex-wrap gap-2">
            {!zustand.connected ? (
              <button
                type="button"
                className={primaer}
                disabled={laeuft}
                onClick={async () => {
                  const text =
                    `${zustand.counts.projectsToMirror} ${terms.projectPlural} werden gespiegelt. ` +
                    `${zustand.counts.unlinkedActiveRefs} ${terms.projectPlural} der Zeiterfassung ohne Verknüpfung werden inaktiv — ihre Buchungen bleiben lesbar. ` +
                    'Vorher übernehmen? Dann jetzt abbrechen.'
                  if (!(await bestaetige(text))) return
                  void tun(async () => setSpiegel((await connectTimeBridge()).mirror))
                }}
              >
                Zeiterfassung anschließen
              </button>
            ) : (
              <>
                <button type="button" className={knopf} disabled={laeuft} onClick={() => void tun(async () => setSpiegel(await rebuildMirror()))}>
                  Spiegel neu aufbauen
                </button>
                <button
                  type="button"
                  className={knopf}
                  disabled={laeuft}
                  onClick={async () => {
                    if (await bestaetige(`Abkoppeln? Die Zeiterfassung pflegt ihre ${terms.projectPlural} dann wieder selbst; Verknüpfungen bleiben.`))
                      void tun(async () => void (await disconnectTimeBridge()))
                  }}
                >
                  Abkoppeln
                </button>
              </>
            )}
          </div>
        )}
        {spiegel && (
          <div className="mt-3 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
            {spiegel.mirrored} gespiegelt
            {spiegel.failed.length > 0 && (
              <ul className="mt-1 list-disc pl-5 text-destructive">
                {spiegel.failed.map((f) => (
                  <li key={f.projectId}>
                    {f.projectNumber}: {MIRROR_CODES[f.code] ?? f.code}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {darf && (
        <div className={formCardSm}>
          <div className="flex flex-wrap items-center gap-3">
            <h2 className={cardTitle}>Nicht verknüpfte {terms.projectPlural} der Zeiterfassung</h2>
            <label className="ml-auto flex items-center gap-1 text-xs">
              <input type="checkbox" checked={mitAusgeblendeten} onChange={(e) => setMitAusgeblendeten(e.target.checked)} /> ausgeblendete zeigen
            </label>
          </div>

          {liste.length === 0 ? (
            <p className="text-sm text-muted-foreground">Keine — alles in der Zeiterfassung ist verknüpft.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-border text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="w-8 py-1">
                        <input
                          type="checkbox"
                          aria-label="alle"
                          checked={auswahl.size > 0 && auswahl.size === liste.length}
                          onChange={(e) => setAuswahl(e.target.checked ? new Set(liste.map((r) => r.id)) : new Set())}
                        />
                      </th>
                      <th className="px-2 py-1">Nummer</th>
                      <th className="px-2 py-1">Bezeichnung</th>
                      <th className="px-2 py-1">Quelle</th>
                      <th className="px-2 py-1 text-right">Buchungen</th>
                      <th className="px-2 py-1">zuletzt</th>
                      <th className="px-2 py-1">Hinweis</th>
                      <th className="px-2 py-1" />
                    </tr>
                  </thead>
                  <tbody>
                    {liste.map((r) => (
                      <tr key={r.id} className={`border-b border-border last:border-0 ${r.ignored ? 'opacity-50' : ''}`}>
                        <td className="px-2 py-1">
                          <input
                            type="checkbox"
                            aria-label={r.projectNumber}
                            checked={auswahl.has(r.id)}
                            onChange={(e) =>
                              setAuswahl((a) => {
                                const n = new Set(a)
                                if (e.target.checked) n.add(r.id)
                                else n.delete(r.id)
                                return n
                              })
                            }
                          />
                        </td>
                        <td className="px-2 py-1 font-mono text-xs">{r.projectNumber}</td>
                        <td className="px-2 py-1">
                          {r.name}
                          <span className="ml-1 text-xs text-muted-foreground">
                            · {r.positions} Pos. · {r.steps} Schritte{r.isActive ? '' : ' · inaktiv'}
                          </span>
                        </td>
                        <td className="px-2 py-1 text-xs">{r.source}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{r.entries}</td>
                        <td className="px-2 py-1 text-xs">{alsTag(r.lastBookedOn)}</td>
                        <td className="px-2 py-1 text-xs">
                          {r.collision && (
                            <span className="text-yellow-700 dark:text-yellow-300">
                              Nummer vorhanden: {r.collision.projectNumber} {r.collision.name}
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1 text-right text-xs">
                          {darfAnlegen && (
                            <>
                              <button type="button" className="text-primary hover:underline" onClick={() => setVerknuepfen(r)}>
                                verknüpfen
                              </button>
                              {' · '}
                              <button
                                type="button"
                                className="text-primary hover:underline"
                                disabled={laeuft}
                                onClick={() =>
                                  void tun(async () => {
                                    const x = await importRefWithNewNumber(r.id)
                                    setErgebnis(`${x.imported.length} übernommen als ${x.imported.map((i) => i.projectNumber).join(', ')}`)
                                  })
                                }
                              >
                                mit neuer Nummer
                              </button>
                              {' · '}
                            </>
                          )}
                          <button type="button" className="text-muted-foreground hover:underline" disabled={laeuft} onClick={() => void tun(() => ignoreRef(r.id, !r.ignored))}>
                            {r.ignored ? 'einblenden' : 'nicht übernehmen'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {darfAnlegen && (
                <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-border pt-3 text-sm">
                  <span>{auswahl.size} gewählt</span>
                  <label className="flex items-center gap-1">
                    <input type="radio" checked={nummern === 'KEEP'} onChange={() => setNummern('KEEP')} /> Nummern behalten
                  </label>
                  <label className="flex items-center gap-1">
                    <input type="radio" checked={nummern === 'NEW'} onChange={() => setNummern('NEW')} /> neue Nummern vergeben
                  </label>
                  <button
                    type="button"
                    className={knopf}
                    disabled={laeuft || auswahl.size === 0}
                    onClick={() => void tun(async () => setVorschau(await importRefs([...auswahl], nummern, true)))}
                  >
                    Vorschau
                  </button>
                </div>
              )}

              {vorschau && (
                <div className="mt-3 space-y-2 rounded-md border border-border bg-muted/40 p-3 text-sm">
                  <p>
                    {vorschau.toImport.length} {vorschau.toImport.length === 1 ? 'wird' : 'werden'} übernommen
                    {vorschau.renumberedSteps > 0 && `, ${vorschau.renumberedSteps} Arbeitsschritte bekommen eine neue Nummer`}.
                  </p>
                  {vorschau.skipped.length > 0 && (
                    <p className="text-yellow-700 dark:text-yellow-300">
                      {vorschau.skipped.length} nicht übernommen — Nummer schon vorhanden: {vorschau.skipped.map((s) => s.projectNumber).join(', ')}
                    </p>
                  )}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className={primaer}
                      disabled={laeuft || vorschau.toImport.length === 0}
                      onClick={() =>
                        void tun(async () => {
                          const x = await importRefs(vorschau.toImport.map((t) => t.refId), nummern, false)
                          setVorschau(null)
                          setAuswahl(new Set())
                          setErgebnis(
                            `${x.imported.length} übernommen` +
                              (x.failed.length ? `, ${x.failed.length} gescheitert (${x.failed.map((f) => f.projectNumber).join(', ')})` : ''),
                          )
                        })
                      }
                    >
                      Übernehmen
                    </button>
                    <button type="button" className={knopf} onClick={() => setVorschau(null)}>
                      Abbrechen
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          {ergebnis && <p className="mt-2 text-sm text-green-700 dark:text-green-300">{ergebnis}</p>}
        </div>
      )}

      {fehler && <p className="text-sm text-destructive">{fehler}</p>}

      {verknuepfen && (
        <VerknuepfenDialog
          ref_={verknuepfen}
          onClose={() => setVerknuepfen(null)}
          onLink={(projectId) =>
            void tun(async () => {
              await linkRef(verknuepfen.id, projectId)
              setVerknuepfen(null)
              setErgebnis(`${verknuepfen.projectNumber} verknüpft`)
            })
          }
        />
      )}
    </div>
  )
}

function VerknuepfenDialog({ ref_, onClose, onLink }: { ref_: UnlinkedRef; onClose: () => void; onLink: (projectId: string) => void }) {
  const terms = useTerminology()
  const [suche, setSuche] = useState(ref_.collision?.projectNumber ?? '')
  const [treffer, setTreffer] = useState<{ id: string; projectNumber: string; name: string }[]>([])

  useEffect(() => {
    const t = setTimeout(() => {
      searchProjectsForLink(suche).then(setTreffer).catch(() => setTreffer([]))
    }, 250)
    return () => clearTimeout(t)
  }, [suche])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg space-y-3 rounded-lg border border-border bg-background p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-primary">
          {ref_.projectNumber} {ref_.name} verknüpfen
        </h2>
        <p className="text-sm text-muted-foreground">
          Positionen werden über die Nummer verbunden, übrige neu angelegt. Die Buchungen zählen danach als Ist-Stunden.
        </p>
        <input className={inputBase} autoFocus placeholder={`${terms.project} suchen`} value={suche} onChange={(e) => setSuche(e.target.value)} />
        <ul className="max-h-64 overflow-y-auto rounded-md border border-border">
          {treffer.map((p) => (
            <li key={p.id}>
              <button type="button" className="w-full px-3 py-1.5 text-left text-sm hover:bg-muted" onClick={() => onLink(p.id)}>
                <span className="mr-2 font-mono text-xs text-muted-foreground">{p.projectNumber}</span>
                {p.name}
              </button>
            </li>
          ))}
          {treffer.length === 0 && <li className="px-3 py-2 text-sm text-muted-foreground">Keine Treffer.</li>}
        </ul>
        <div className="flex justify-end">
          <button type="button" className="rounded-md border border-border px-4 py-2 text-sm" onClick={onClose}>
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  )
}
